'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { storyKeys } from '../queryKeys';
import { subscriptionKeys, PRICING_DEFAULTS } from '@/contexts/subscriptions';
import type { TokenPricingData } from '@/contexts/subscriptions';
import { createStory, startStoryJob, type CreateStoryActionInput } from '../actions/createStory';
import { updateStory } from '../actions/updateStory';
import { deleteStory } from '../actions/deleteStory';

/**
 * Optimistic balance deduction helper.
 * Reads current balance from cache, subtracts cost, returns previous value for rollback.
 */
function optimisticDeductBalance(
  queryClient: ReturnType<typeof useQueryClient>,
  familyId: string,
  cost: number,
) {
  const balanceKey = subscriptionKeys.balance(familyId);
  const previousBalance = queryClient.getQueryData<{ balance: number }>(balanceKey);

  if (previousBalance) {
    queryClient.setQueryData(balanceKey, {
      ...previousBalance,
      balance: Math.max(0, previousBalance.balance - cost),
    });
  }

  return previousBalance;
}

export function useStoryMutations(familyId: string | null | undefined) {
  const queryClient = useQueryClient();

  const create = useMutation({
    mutationFn: (input: CreateStoryActionInput) => createStory(input),
    onMutate: async () => {
      if (!familyId) return;

      // Cancel outgoing balance refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: subscriptionKeys.balance(familyId) });

      // Read storyReservation from pricing cache (prefetched in page.tsx)
      const pricing = queryClient.getQueryData<TokenPricingData>(subscriptionKeys.pricing());
      const cost = pricing?.storyReservation ?? PRICING_DEFAULTS.storyReservation;

      const previousBalance = optimisticDeductBalance(queryClient, familyId, cost);
      return { previousBalance };
    },
    onError: (_err, _vars, context) => {
      // Rollback optimistic balance on error
      if (familyId && context?.previousBalance) {
        queryClient.setQueryData(subscriptionKeys.balance(familyId), context.previousBalance);
      }
    },
    onSuccess: () => {
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: storyKeys.list(familyId) });
      }
    },
    onSettled: () => {
      // Refetch real balance from server
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.balance(familyId) });
      }
    },
  });

  const start = useMutation({
    mutationFn: (jobId: string) => startStoryJob(jobId),
  });

  const update = useMutation({
    mutationFn: (params: { storyId: string; updates: Parameters<typeof updateStory>[1] }) =>
      updateStory(params.storyId, params.updates),
    onSuccess: (result, params) => {
      queryClient.invalidateQueries({ queryKey: storyKeys.detail(params.storyId) });
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: storyKeys.list(familyId) });
      }
    },
  });

  const remove = useMutation({
    mutationFn: (storyId: string) => deleteStory(storyId),
    onSuccess: () => {
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: storyKeys.list(familyId) });
      }
    },
  });

  return {
    createStory: create,
    startStoryJob: start,
    updateStory: update,
    deleteStory: remove,
  };
}
